/**
 * Bespoke design form: photos are shrunk on the device and uploaded one by
 * one to Purecious OS Blob storage as soon as they're picked; the submit
 * then sends the answers plus the photo URLs as JSON. Every photo is
 * re-encoded as JPEG in the browser (Safari on iPhone decodes HEIC), so
 * what lands in the CRM opens anywhere; the server converts any HEIC that
 * a browser couldn't decode.
 */

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;
const RAW_LIMIT = 4 * 1024 * 1024; // server refuses bigger raw files

async function shrinkToJpeg(file) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // The browser can't decode this format (HEIC on Chrome): send it as is
    // and let the server convert, as long as it is small enough.
    if (file.size <= RAW_LIMIT) return file;
    throw new Error("This photo is too large. Please choose a JPG or PNG under 4 MB.");
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
  if (!blob) throw new Error("Couldn't process this photo.");
  const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
}

class BespokeForm extends HTMLElement {
  connectedCallback() {
    this.endpoint = (this.dataset.endpoint || "").replace(/\/$/, "");
    this.token = this.dataset.token || "";
    this.maxPhotos = Number(this.dataset.maxPhotos || 6);
    this.photos = []; // { id, file, url, status, li }

    this.form = this.querySelector("[data-form]");
    this.fileInput = this.querySelector("[data-file-input]");
    this.dropzone = this.querySelector("[data-dropzone]");
    this.previews = this.querySelector("[data-previews]");
    this.photoError = this.querySelector("[data-photo-error]");
    this.errorBanner = this.querySelector("[data-error]");
    this.successBanner = this.querySelector("[data-success]");

    this.abort = new AbortController();
    const { signal } = this.abort;
    // The contact form has no photo controls; only wire them when present.
    if (this.fileInput && this.dropzone) {
      this.querySelector("[data-browse]")?.addEventListener("click", () => this.fileInput.click(), { signal });
      this.fileInput.addEventListener("change", () => this.addFiles(this.fileInput.files), { signal });
      this.dropzone.addEventListener("dragover", (e) => { e.preventDefault(); this.dropzone.classList.add("is-dragover"); }, { signal });
      this.dropzone.addEventListener("dragleave", () => this.dropzone.classList.remove("is-dragover"), { signal });
      this.dropzone.addEventListener("drop", (e) => {
        e.preventDefault();
        this.dropzone.classList.remove("is-dragover");
        this.addFiles(e.dataTransfer?.files);
      }, { signal });
    }
    this.form.addEventListener("submit", (e) => this.submit(e), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  showPhotoError(message) {
    this.photoError.textContent = message || "";
    this.photoError.hidden = !message;
  }

  async addFiles(fileList) {
    this.showPhotoError("");
    const files = Array.from(fileList || []).filter((f) => /^image\//.test(f.type) || /\.(heic|heif)$/i.test(f.name));
    this.fileInput.value = "";
    for (const file of files) {
      if (this.photos.length >= this.maxPhotos) {
        this.showPhotoError(`You can add up to ${this.maxPhotos} photos.`);
        break;
      }
      const photo = { id: Math.random().toString(36).slice(2), file, url: null, status: "uploading" };
      this.photos.push(photo);
      this.renderPreview(photo);
      this.uploadPhoto(photo);
    }
  }

  renderPreview(photo) {
    const li = document.createElement("li");
    li.className = "bespoke-form__preview";
    li.dataset.state = photo.status;
    const img = document.createElement("img");
    img.alt = "";
    // HEIC won't render as a thumbnail in most browsers; a blank tile is fine.
    img.src = URL.createObjectURL(photo.file);
    img.onerror = () => { img.removeAttribute("src"); };
    const status = document.createElement("span");
    status.className = "bespoke-form__preview-status";
    status.textContent = "Uploading…";
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "bespoke-form__remove";
    remove.setAttribute("aria-label", "Remove photo");
    remove.textContent = "×";
    remove.addEventListener("click", () => this.removePhoto(photo));
    li.append(img, status, remove);
    photo.li = li;
    photo.statusEl = status;
    this.previews.append(li);
    this.previews.hidden = false;
  }

  removePhoto(photo) {
    this.photos = this.photos.filter((p) => p !== photo);
    photo.li?.remove();
    this.previews.hidden = this.photos.length === 0;
  }

  async uploadPhoto(photo) {
    try {
      const file = await shrinkToJpeg(photo.file);
      const body = new FormData();
      body.append("file", file, file.name);
      const res = await fetch(`${this.endpoint}/api/public/bespoke-form/upload`, {
        method: "POST",
        headers: { "X-Form-Token": this.token },
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || "Upload failed");
      photo.url = data.url;
      photo.status = "done";
      photo.li.dataset.state = "done";
      photo.statusEl.remove();
    } catch (err) {
      photo.status = "error";
      photo.li.dataset.state = "error";
      photo.statusEl.textContent = "Failed";
      this.showPhotoError(err.message || "Couldn't upload a photo. Remove it and try again.");
    }
  }

  async submit(event) {
    event.preventDefault();
    this.errorBanner.hidden = true;
    if (!this.form.reportValidity()) return;
    if (this.photos.some((p) => p.status === "uploading")) {
      this.showError("Your photos are still uploading — one moment.");
      return;
    }
    if (this.photos.some((p) => p.status === "error")) {
      this.showError("One of the photos failed to upload. Remove it (×) and try again.");
      return;
    }

    const data = Object.fromEntries(new FormData(this.form).entries());
    data.photos = this.photos.map((p) => p.url).filter(Boolean);
    data.form = this.dataset.formKind || "bespoke";
    data.submission_id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    data.page = location.pathname;

    this.form.classList.add("is-sending");
    try {
      const res = await fetch(`${this.endpoint}/api/public/bespoke-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Form-Token": this.token },
        body: JSON.stringify(data),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok || !result.ok) throw new Error(result.error || "Something went wrong");
      this.form.hidden = true;
      this.successBanner.hidden = false;
      this.successBanner.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (err) {
      this.showError(
        /fetch|network/i.test(err.message)
          ? "We couldn't send your request. Please check your connection and try again."
          : err.message
      );
    } finally {
      this.form.classList.remove("is-sending");
    }
  }

  showError(message) {
    this.errorBanner.textContent = message;
    this.errorBanner.hidden = false;
    this.errorBanner.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

if (!customElements.get("bespoke-form")) customElements.define("bespoke-form", BespokeForm);
